import { useI18n } from '../../../i18n';
import { cn } from '../../../utils/cn';

// ============================================
// Types
// ============================================

export type CustomerTier = 'ELITE' | 'PLATINUM' | 'GOLD' | 'SILVER' | 'BRONZE' | 'NONE';

interface TierBadgeProps {
  tier: CustomerTier;
  className?: string;
}

// ============================================
// Styles
// ============================================

const tierStyles: Record<CustomerTier, { bg: string; border: string; text: string }> = {
  ELITE: {
    bg: 'bg-[#FFFBEB]',
    border: 'border-[#FDE68A]',
    text: 'text-[#B45309]',
  },
  PLATINUM: {
    bg: 'bg-[#F1F5F9]',
    border: 'border-[#E2E8F0]',
    text: 'text-[#475569]',
  },
  GOLD: {
    bg: 'bg-[#FFF7ED]',
    border: 'border-[#FED7AA]',
    text: 'text-[#EA580C]',
  },
  SILVER: {
    bg: 'bg-[#F3F4F6]',
    border: 'border-[#E5E7EB]',
    text: 'text-[#4B5563]',
  },
  BRONZE: {
    bg: 'bg-[#FFF7ED]',
    border: 'border-[#FFEDD5]',
    text: 'text-[#C2410C]',
  },
  NONE: {
    bg: 'bg-[#E1E2ED]',
    border: 'border-[#C3C6D7]',
    text: 'text-[#737686]',
  },
};

// ============================================
// Component
// ============================================

export function TierBadge({ tier, className }: TierBadgeProps) {
  const { t } = useI18n();
  const styles = tierStyles[tier] || tierStyles.NONE;

  const labelMap: Record<CustomerTier, string> = {
    ELITE: t('elite'),
    PLATINUM: t('platinum'),
    GOLD: t('gold'),
    SILVER: t('silver') || 'Silver',
    BRONZE: t('bronze') || 'Bronze',
    NONE: t('none'),
  };

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5',
        'text-[10px] font-black uppercase tracking-[1px]',
        styles.bg,
        styles.border,
        styles.text,
        className
      )}
    >
      {labelMap[tier] || tier}
    </span>
  );
}
